"use client";

import { AutomatedContentTile, getContentToDisplay } from "@2u-landing-pages/components/AutomatedContent";
import { Tile } from "@2u-landing-pages/components/Tile";
import { TileProps } from "@2u-landing-pages/components/Tile/Tile.types";
import { PricingApiContext, useProgramMeta } from "@2u-landing-pages/providers";
import { Container } from "@red-digital/bricks";
import cn from "classnames";
import React, { CSSProperties, useContext, useMemo } from "react";
import { BentoItem, BentoItemGroup, TilesProps } from "./Bento.types";
// @ts-ignore
import styles from "./Tiles.module.scss";

const isTile = (item: BentoItem): item is Extract<BentoItem, { sys: { contentType: { sys: { id: "tile" } } } }> =>
  item?.sys?.contentType?.sys?.id === "tile";

const isAutomatedContentTile = (
  item: BentoItem,
): item is Extract<BentoItem, { sys: { contentType: { sys: { id: "automated_content_tile" } } } }> =>
  item?.sys?.contentType?.sys?.id === "automated_content_tile";

type TilesCSSProperties = CSSProperties & {
  "--total-visible-tiles"?: number;
  "--visible-in-group"?: number;
};

export const Tiles: React.FC<TilesProps> = ({
  variation,
  embedded,
  groups,
  tile_background_color,
  phoneNumber,
}) => {
  const isExtended = variation === "extended";
  const isInline = variation === "inline";
  const programMeta = useProgramMeta();
  const { pricingApiValues } = useContext(PricingApiContext);

  const tileCfg = {
    padded: Boolean(tile_background_color),
    desktopOrientation: "vertical",
    mobileOrientation: "vertical",
    textAlign: isInline ? "left" : undefined,
    heading: isInline ? { variant: "heading-lg" } : undefined,
    subhead: isInline ? { variant: "subhead-sm" } : undefined,
  } satisfies Partial<TileProps>;

  const { visibleCounts, totalVisible } = useMemo(() => {
    const isItemVisible = (item: BentoItem): boolean => {
      if (isTile(item)) return true;
      if (isAutomatedContentTile(item)) {
        const content = getContentToDisplay({
          ...item.fields,
          metaStartDate: programMeta?.fields?.start_date,
          pricingApiStartDate: pricingApiValues?.startDate,
        });
        return Boolean(content);
      }
      return false;
    };

    const counts = groups.map((group) => (group ?? []).filter(isItemVisible).length);
    return { visibleCounts: counts, totalVisible: counts.reduce((sum, n) => sum + n, 0) };
  }, [groups, programMeta, pricingApiValues]);

  return (
    <Container
      flex
      direction="column"
      wrap={embedded && groups?.length >= 2 ? "wrap" : "nowrap"}
      lg={{ direction: "row" }}
      className={styles.tileContainer}
      style={{ "--total-visible-tiles": totalVisible || 1 } as TilesCSSProperties}
    >
      {groups.map(
        (group: BentoItemGroup, index: number) =>
          Boolean(group?.length) && (
            <Container
              flex
              key={index}
              className={cn(styles.group, {
                [styles.groupEmbedded]: !isExtended && embedded && (group || []).length > 1,
                [styles.groupEmbeddedExtended]: isExtended && embedded && groups?.length > 1,
                [styles.groupFullWidth]: embedded && index === 2 && (group || []).length > 1,
              })}
              style={{ "--visible-in-group": visibleCounts[index] ?? 0 } as TilesCSSProperties}
            >
              {group?.map((item, idx: number) => {
                return isTile(item) ? (
                  <Tile
                    key={idx}
                    className={styles.tile}
                    content={item}
                    {...tileCfg}
                    phoneNumber={phoneNumber}
                  />
                ) : isAutomatedContentTile(item) ? (
                  <AutomatedContentTile
                    id={item.sys.id}
                    key={idx}
                    {...item.fields}
                    tileConfig={tileCfg}
                    className={styles.tile}
                    phoneNumber={phoneNumber}
                    />
                ) : null;
              })}
            </Container>
          ),
      )}
    </Container>
  );
};
